import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Pin,
  PinOff,
  Edit2,
  Trash2,
  MoreVertical,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { updateNote, toggleNotePin, deleteNote, type Note } from '@/lib/api';
import { getInitials } from '@/lib/utils';

interface NoteCardProps {
  note: Note;
  currentUserId: string;
  currentUserRole: string;
  entityType: 'candidate' | 'job' | 'application' | 'company';
  entityId: string;
  className?: string;
}

function renderContentWithMentions(content: string): React.ReactNode {
  // Parse @[Name](userId) format mentions, with fallback for legacy @Name format
  const parts = content.split(/(@\[[^\]]+\]\([^)]+\)|@\w+(?:\s+\w+)?)/g);
  return parts.map((part, index) => {
    // New format: @[FirstName LastName](userId)
    const structuredMatch = part.match(/^@\[([^\]]+)\]\(([^)]+)\)$/);
    if (structuredMatch) {
      return (
        <span key={index} className="text-emerald-500 font-medium">
          @{structuredMatch[1]}
        </span>
      );
    }
    // Legacy format: @FirstName LastName
    if (part.startsWith('@')) {
      return (
        <span key={index} className="text-emerald-500 font-medium">
          {part}
        </span>
      );
    }
    return part;
  });
}

export function NoteCard({
  note,
  currentUserId,
  currentUserRole,
  entityType,
  entityId,
  className,
}: NoteCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const queryClient = useQueryClient();

  const isOwner = note.createdBy.id === currentUserId;
  const isAdmin = currentUserRole === 'admin';
  const canEdit = isOwner || isAdmin;
  const canDelete = isOwner || isAdmin;
  const canPin = isAdmin;

  const invalidateNotes = () => {
    queryClient.invalidateQueries({ queryKey: ['notes', entityType, entityId] });
  };

  const updateMutation = useMutation({
    mutationFn: (content: string) => updateNote(note.id, { content }),
    onSuccess: () => {
      invalidateNotes();
      setIsEditing(false);
    },
  });

  const togglePinMutation = useMutation({
    mutationFn: () => toggleNotePin(note.id),
    onSuccess: invalidateNotes,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteNote(note.id),
    onSuccess: () => {
      invalidateNotes();
      setShowDeleteConfirm(false);
    },
  });

  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== note.content) {
      updateMutation.mutate(editContent.trim());
    } else {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(note.content);
    setIsEditing(false);
  };

  return (
    <div
      className={cn(
        'rounded-lg border bg-bg-tertiary p-4',
        note.isPinned ? 'border-amber-300 bg-amber-50/50' : 'border-white/10',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            {note.createdBy.avatarUrl && (
              <AvatarImage src={note.createdBy.avatarUrl} />
            )}
            <AvatarFallback className="text-xs">
              {getInitials(note.createdBy.firstName, note.createdBy.lastName)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">
                {note.createdBy.firstName} {note.createdBy.lastName}
              </span>
              {note.isPinned && (
                <Pin className="h-3.5 w-3.5 text-amber-600 fill-amber-600" />
              )}
            </div>
            <span className="text-xs text-text-tertiary">
              {formatRelativeTime(note.createdAt)}
              {note.updatedAt !== note.createdAt && ' (edited)'}
            </span>
          </div>
        </div>

        {/* Actions Menu */}
        {(canEdit || canDelete || canPin) && (
          <div className="relative">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 z-50 mt-1 w-36 rounded-md border bg-bg-tertiary shadow-lg">
                  {canPin && (
                    <button
                      type="button"
                      onClick={() => {
                        togglePinMutation.mutate();
                        setShowMenu(false);
                      }}
                      disabled={togglePinMutation.isPending}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-white/10"
                    >
                      {note.isPinned ? (
                        <>
                          <PinOff className="h-4 w-4" />
                          Unpin
                        </>
                      ) : (
                        <>
                          <Pin className="h-4 w-4" />
                          Pin note
                        </>
                      )}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(true);
                        setShowMenu(false);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-white/10"
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteConfirm(true);
                        setShowMenu(false);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="mt-3">
        {isEditing ? (
          <div className="space-y-3">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[80px]"
              autoFocus
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancelEdit}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending || !editContent.trim()}
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-text-secondary whitespace-pre-wrap">
            {renderContentWithMentions(note.content)}
          </p>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Note
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
